import { isRecord, parseNoticePayload, readString } from './notice-payload';
import type {
  RawOpenIMConversation,
  RawOpenIMMessage,
  WorkspaceIMConversation,
  WorkspaceIMMessage,
} from './openim-types';

const SINGLE_SESSION_TYPE = 1;
const GROUP_SESSION_TYPE = 2;
const TEXT_MESSAGE_TYPE = 101;
const CUSTOM_MESSAGE_TYPE = 110;

/** toFiniteNumber 将字符串或数字归一化为有限数值。 */
function toFiniteNumber(value: unknown, fallback = 0): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

/** normalizeTimestamp 兼容秒和毫秒时间戳，统一为毫秒。 */
export function normalizeTimestamp(value: unknown): number {
  const timestamp = toFiniteNumber(value);
  if (timestamp <= 0) return 0;
  return timestamp < 10_000_000_000 ? timestamp * 1000 : timestamp;
}

/** hashText 为缺失正式消息 ID 的历史数据生成稳定、非加密降级摘要。 */
function hashText(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

/** unwrapEventData 去掉 SDK callback 的 data 包装，保留直接 payload 兼容性。 */
export function unwrapEventData(value: unknown): unknown {
  return isRecord(value) && 'data' in value ? value.data : value;
}

/** asArray 从 SDK 的数组或 data 包装中读取列表。 */
export function asArray(value: unknown): unknown[] {
  const unwrapped = unwrapEventData(value);
  return Array.isArray(unwrapped) ? unwrapped : [];
}

/** readMessageText 读取文本或自定义通知的安全摘要。 */
function readMessageText(raw: RawOpenIMMessage): string {
  if (toFiniteNumber(raw.contentType) === TEXT_MESSAGE_TYPE && isRecord(raw.textElem)) {
    return readString(raw.textElem.content) ?? readString(raw.content) ?? '';
  }
  if (toFiniteNumber(raw.contentType) === CUSTOM_MESSAGE_TYPE && isRecord(raw.customElem)) {
    const notice = parseNoticePayload(raw.customElem.data ?? raw.content);
    return notice.title || notice.content;
  }
  return readString(raw.content) ?? '';
}

/** parseLatestMessage 从会话 latestMsg JSON 中提取列表摘要。 */
function parseLatestMessage(value: unknown): string {
  if (isRecord(value)) return readMessageText(value);
  if (typeof value !== 'string' || !value) return '';
  try {
    const parsed: unknown = JSON.parse(value);
    return isRecord(parsed) ? readMessageText(parsed) : value;
  } catch {
    return value;
  }
}

/** inferSessionType 从消息或目标会话推断会话类型，兼容 SDK 历史消息缺少 sessionType 的情况。 */
function inferSessionType(raw: RawOpenIMMessage, conversationID: string): number {
  const sessionType = toFiniteNumber(raw.sessionType);
  if (sessionType === SINGLE_SESSION_TYPE || sessionType === GROUP_SESSION_TYPE) return sessionType;
  const rawConversationID = readString(raw.conversationID) ?? conversationID;
  if (rawConversationID.startsWith('sg_')) return GROUP_SESSION_TYPE;
  if (rawConversationID.startsWith('si_')) return SINGLE_SESSION_TYPE;
  return 0;
}

/** normalizeConversations 保留单聊通知和直播群聊，具体页面再按业务筛选。 */
export function normalizeConversations(value: unknown): WorkspaceIMConversation[] {
  return asArray(value).flatMap((item) => {
    if (!isRecord(item)) return [];
    const raw: RawOpenIMConversation = item;
    const conversationType = toFiniteNumber(raw.conversationType);
    if (conversationType !== SINGLE_SESSION_TYPE && conversationType !== GROUP_SESSION_TYPE) return [];
    const conversationID = readString(raw.conversationID);
    if (!conversationID) return [];
    return [{
      avatar: readString(raw.faceURL),
      conversationID,
      conversationType: conversationType === GROUP_SESSION_TYPE ? 'group' : 'single',
      latestMessage: parseLatestMessage(raw.latestMsg),
      latestMessageTime: normalizeTimestamp(raw.latestMsgSendTime),
      name: readString(raw.showName) ?? (conversationType === GROUP_SESSION_TYPE ? '直播交流' : '系统通知'),
      unreadCount: Math.max(0, toFiniteNumber(raw.unreadCount)),
      userID: readString(raw.userID) ?? '',
    }];
  });
}

/** getMessageID 按服务端、客户端、序号和稳定降级摘要的优先级生成幂等键。 */
export function getMessageID(raw: RawOpenIMMessage, conversationID: string): string {
  const serverMsgID = readString(raw.serverMsgID);
  if (serverMsgID) return `server:${serverMsgID}`;
  const clientMsgID = readString(raw.clientMsgID);
  if (clientMsgID) return `client:${clientMsgID}`;
  const seq = toFiniteNumber(raw.seq);
  if (seq > 0) return `seq:${conversationID}:${seq}`;
  const signature = [
    conversationID,
    normalizeTimestamp(raw.sendTime ?? raw.createTime),
    readString(raw.sendID) ?? '',
    readString(raw.content) ?? '',
  ].join('|');
  return `fallback:${hashText(signature)}`;
}

/** normalizeMessage 把 SDK 消息转换为 Remote 可消费且不包含原始 SDK 对象的模型。 */
export function normalizeMessage(
  value: unknown,
  conversationID: string,
  currentUserID: string,
): WorkspaceIMMessage | undefined {
  if (!isRecord(value)) return undefined;
  const raw: RawOpenIMMessage = value;
  const sessionType = inferSessionType(raw, conversationID);
  if (sessionType !== SINGLE_SESSION_TYPE && sessionType !== GROUP_SESSION_TYPE) return undefined;
  const contentType = toFiniteNumber(raw.contentType);
  const customData = isRecord(raw.customElem) ? raw.customElem.data : raw.content;
  const notice = contentType === CUSTOM_MESSAGE_TYPE
    ? parseNoticePayload(customData)
    : undefined;
  const content = contentType === TEXT_MESSAGE_TYPE
    ? readMessageText(raw)
    : notice?.content || readMessageText(raw);
  const sender = readString(raw.sendID) ?? '';
  return {
    clientMsgID: readString(raw.clientMsgID),
    content,
    contentType,
    conversationID,
    id: getMessageID(raw, conversationID),
    isMine: sender === currentUserID,
    notice,
    receiver: readString(raw.recvID) ?? '',
    sendTime: normalizeTimestamp(raw.sendTime ?? raw.createTime),
    sender,
    senderAvatar: readString(raw.senderFaceUrl),
    senderName: readString(raw.senderNickname) ?? notice?.senderName,
    serverMsgID: readString(raw.serverMsgID),
  };
}

/** normalizeMessages 归一化、按幂等键去重并按发送时间升序排列。 */
export function normalizeMessages(
  value: unknown,
  conversationID: string,
  currentUserID: string,
): WorkspaceIMMessage[] {
  const unwrapped = unwrapEventData(value);
  let list: unknown[] = [];
  if (isRecord(unwrapped) && Array.isArray(unwrapped.messageList)) {
    list = unwrapped.messageList;
  } else if (Array.isArray(unwrapped)) {
    list = unwrapped;
  }
  const messages: WorkspaceIMMessage[] = [];
  for (const item of list) {
    const message = normalizeMessage(item, conversationID, currentUserID);
    if (message) messages.push(message);
  }
  return dedupeMessages(messages).sort((left, right) => (
    left.sendTime - right.sendTime || left.id.localeCompare(right.id)
  ));
}

/** getMessageAliases 返回服务端与客户端 ID 别名，用于双事件或同步补齐 serverMsgID 时去重。 */
function getMessageAliases(message: WorkspaceIMMessage): string[] {
  return [
    message.id,
    message.serverMsgID ? `server:${message.serverMsgID}` : '',
    message.clientMsgID ? `client:${message.clientMsgID}` : '',
  ].filter(Boolean);
}

/** dedupeMessages 按任一正式 ID 别名合并消息，并优先保留带 serverMsgID 的完整版本。 */
function dedupeMessages(messages: readonly WorkspaceIMMessage[]): WorkspaceIMMessage[] {
  const result: WorkspaceIMMessage[] = [];
  for (const message of messages) {
    const aliases = new Set(getMessageAliases(message));
    const index = result.findIndex(item => getMessageAliases(item).some(alias => aliases.has(alias)));
    if (index < 0) {
      result.push(message);
      continue;
    }
    if (message.serverMsgID || !result[index]?.serverMsgID) result[index] = message;
  }
  return result;
}

/** mergeMessages 合并历史页与实时刷新窗口，并保证同一消息只保留一条。 */
export function mergeMessages(
  current: readonly WorkspaceIMMessage[],
  incoming: readonly WorkspaceIMMessage[],
): WorkspaceIMMessage[] {
  return dedupeMessages([...current, ...incoming]).sort((left, right) => (
    left.sendTime - right.sendTime || left.id.localeCompare(right.id)
  ));
}

/** readHistoryEnd 从 SDK 历史响应读取是否已到最早一页。 */
export function readHistoryEnd(value: unknown): boolean {
  const unwrapped = unwrapEventData(value);
  return isRecord(unwrapped) && unwrapped.isEnd === true;
}
